import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getToken,
  getStoredRole,
  setSession,
  clearSession,
  setUnauthorizedHandler,
} from '../../shared/api/session';
import { login as loginRequest, fetchMe } from './api';
import { AuthContext, type AuthValue } from './context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(getToken());

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: !!token,
    retry: false,
  });

  // 전역 401 → 세션 정리 후 로그인으로(client는 emit만, 이동은 여기서).
  // me 401도 이 경로로 처리되므로 별도 에러 effect는 불필요.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      setToken(null);
      qc.clear();
      navigate('/login');
    });
    return () => setUnauthorizedHandler(null);
  }, [qc, navigate]);

  const login = async (username: string, password: string) => {
    const res = await loginRequest({ username, password });
    setSession(res.access_token, res.role);
    setToken(res.access_token);
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    navigate('/');
  };

  const logout = () => {
    clearSession();
    setToken(null);
    qc.clear();
    navigate('/login');
  };

  const user = meQuery.data;
  const value: AuthValue = {
    user,
    role: user?.role ?? getStoredRole(),
    isAuthenticated: !!user,
    isLoading: !!token && meQuery.isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
